import { useState, useEffect } from 'react';
import { Award, Save } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface LoyaltyConfig {
  enabled: boolean;
  points_per_dollar: number;
  welcome_bonus: number;
  birthday_bonus: number;
  referral_bonus: number;
  redemption_rate: number;
  minimum_redemption: number;
  tier_enabled: boolean;
  tiers: Array<{
    name: string;
    min_points: number;
    discount_percentage: number;
  }>;
}

const DEFAULTS: LoyaltyConfig = {
  enabled: false,
  points_per_dollar: 10,
  welcome_bonus: 100,
  birthday_bonus: 200,
  referral_bonus: 500,
  redemption_rate: 100,
  minimum_redemption: 500,
  tier_enabled: false,
  tiers: [
    { name: 'Bronze', min_points: 0, discount_percentage: 0 },
    { name: 'Silver', min_points: 1000, discount_percentage: 5 },
    { name: 'Gold', min_points: 5000, discount_percentage: 10 },
    { name: 'Platinum', min_points: 10000, discount_percentage: 15 },
  ],
};

export default function UpsellLoyalty() {
  const [config, setConfig] = useState<LoyaltyConfig>(DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  async function fetchConfig() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ settings: Record<string, any> }>('/admin/settings');
      const saved = response.settings.upsell_config?.loyalty;
      if (saved) {
        setConfig({ ...DEFAULTS, ...saved });
      }
    } catch (err) {
      setError('Failed to load loyalty program settings. Please try again.');
      console.error('Error fetching loyalty config:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMessage(null);

    try {
      setIsSaving(true);
      setError(null);
      const currentSettings = await api.get<{ settings: Record<string, any> }>('/admin/settings');
      const currentUpsellConfig = currentSettings.settings.upsell_config || {};

      await api.put('/admin/settings', {
        settings: {
          upsell_config: {
            ...currentUpsellConfig,
            loyalty: config,
          },
        },
      });
      setSuccessMessage('Loyalty program settings saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('Error saving loyalty config:', err);
    } finally {
      setIsSaving(false);
    }
  }

  function handleChange<K extends keyof LoyaltyConfig>(field: K, value: LoyaltyConfig[K]) {
    setConfig(prev => ({ ...prev, [field]: value }));
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading loyalty program settings...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Award className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">Loyalty Program</h1>
        </div>
        <p className="text-gray-600">Reward repeat customers with points and exclusive perks</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="status" aria-live="polite">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      <form onSubmit={handleSave} noValidate>
        <div className="space-y-6">
          <Card title="Enable Program" padding="lg">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="loyalty_enabled"
                checked={config.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="loyalty_enabled" className="text-sm font-medium text-gray-700">
                Enable loyalty rewards program
              </label>
            </div>
          </Card>

          <Card title="Points Earning Rules" padding="lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Points Per Dollar Spent"
                type="number"
                value={String(config.points_per_dollar)}
                onChange={(e) => handleChange('points_per_dollar', parseInt(e.target.value) || 10)}
                min="1"
                helperText="Points earned for every $1 spent"
              />
              <Input
                label="Welcome Bonus"
                type="number"
                value={String(config.welcome_bonus)}
                onChange={(e) => handleChange('welcome_bonus', parseInt(e.target.value) || 0)}
                min="0"
                helperText="Points for new sign-ups"
              />
              <Input
                label="Birthday Bonus"
                type="number"
                value={String(config.birthday_bonus)}
                onChange={(e) => handleChange('birthday_bonus', parseInt(e.target.value) || 0)}
                min="0"
                helperText="Points on customer's birthday"
              />
              <Input
                label="Referral Bonus"
                type="number"
                value={String(config.referral_bonus)}
                onChange={(e) => handleChange('referral_bonus', parseInt(e.target.value) || 0)}
                min="0"
                helperText="Points for successful referrals"
              />
            </div>
          </Card>

          <Card title="Points Redemption" padding="lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Points Per Dollar Value"
                type="number"
                value={String(config.redemption_rate)}
                onChange={(e) => handleChange('redemption_rate', parseInt(e.target.value) || 100)}
                min="1"
                helperText="Points needed to redeem $1"
              />
              <Input
                label="Minimum Redemption"
                type="number"
                value={String(config.minimum_redemption)}
                onChange={(e) => handleChange('minimum_redemption', parseInt(e.target.value) || 0)}
                min="0"
                helperText="Minimum points to redeem"
              />
            </div>
          </Card>

          <Card title="Loyalty Tiers" padding="lg">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="tier_enabled"
                  checked={config.tier_enabled}
                  onChange={(e) => handleChange('tier_enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="tier_enabled" className="text-sm font-medium text-gray-700">
                  Enable tier-based rewards
                </label>
              </div>

              {config.tier_enabled && (
                <div className="space-y-4">
                  <p className="text-sm text-stone-600">
                    Customers unlock higher tiers and exclusive discounts as they earn points
                  </p>
                  <div className="border border-stone-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className="text-left py-2 px-3 font-medium text-stone-700">Tier Name</th>
                          <th className="text-left py-2 px-3 font-medium text-stone-700">Min Points</th>
                          <th className="text-left py-2 px-3 font-medium text-stone-700">Discount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {config.tiers.map((tier, index) => (
                          <tr key={index} className="border-t border-stone-200">
                            <td className="py-2 px-3 text-stone-900">{tier.name}</td>
                            <td className="py-2 px-3 text-stone-600">{tier.min_points.toLocaleString()}</td>
                            <td className="py-2 px-3 text-stone-600">{tier.discount_percentage}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isSaving}
          >
            <Save className="w-5 h-5 mr-2" aria-hidden="true" />
            Save Settings
          </Button>
        </div>
      </form>
    </div>
  );
}
